import type { ChatMessageMessageId } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import {
  ChatMessagePayloadType,
  type ChatMessage,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type {
  Chat,
  ChatChatId,
  SendChatParams,
} from '@/contexts/chat/scripts/hooks/useChats/types';
import type { chat } from '@/contexts/chat/scripts/protobuf/protobufs/chat';
import type {
  ChatSocketFlags,
  ChatSocketRes,
} from '@/contexts/chat/scripts/hooks/useChatSocket/types';
import type { RetryOptions } from '@/scripts/utils/queue';
import { isMeMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/utils';
import type { SetReplyMessage } from '@/scripts/hooks/useReply';
import styles from './index.module.scss';

interface RefsProps {
  chat: Chat;
  sendAndDeleteMessages: (
    sendChatParams: SendChatParams,
    params: chat.ISendMessageRequest,
    flags?: ChatSocketFlags,
    retryOptions?: RetryOptions
  ) => Promise<ChatSocketRes>;
  deleteMessagesByMessageIds: (
    chatId: ChatChatId,
    messageIds: ChatMessageMessageId[]
  ) => Promise<void>;
  setReplyMessage?: SetReplyMessage;
}

export const deleteMessageModal = (
  isGroup: boolean,
  isGroupAdmin: boolean,
  message: ChatMessage,
  {
    chat,
    sendAndDeleteMessages,
    deleteMessagesByMessageIds,
    setReplyMessage,
  }: RefsProps
) => {
  let deleteAll = false;

  const Content = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: (value: boolean) => void;
  }) => {
    const [value, setValue] = useState(checked);
    if (!isGroup || isGroupAdmin || isMeMessage(message)) {
      return (
        <Checkbox
          className={styles.checkbox}
          checked={value}
          onChange={(e) => {
            setValue(e.target.checked);
            onChange?.(e.target.checked);
          }}
        >
          <div className={styles.checkboxContent}>Also delete for {chat.name}</div>
        </Checkbox>
      );
    }
    return null;
  };

  Dialog({
    title: 'Do you want to delete this message?',
    confirmText: 'Delete',
    maskClosable: true,
    content: (
      <Content
        checked={deleteAll}
        onChange={(checked) => {
          deleteAll = checked;
        }}
      />
    ),
    onConfirm: async () => {
      if (deleteAll) {
        const res = await sendAndDeleteMessages(chat, {
          payloadType: ChatMessagePayloadType.DELETE_MESSAGE,
          delList: [message.message_id],
          payload: '',
        });
        setReplyMessage?.({ deletedMessage: message });
        return res;
      } else {
        const res = await deleteMessagesByMessageIds(chat.chat_id, [
          message.message_id,
        ]);
        setReplyMessage?.(null);
        return res;
      }
    },
  });
};
